import { ThemeProvider } from '@fluentui/react';
import { FluentProvider, Theme, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { PortalCompatProvider } from '@fluentui/react-portal-compat';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { darkTheme, lightTheme } from 'themes/runtime/globalThemes-v8';
import { darkThemeV9, lightThemeV9 } from 'themes/runtime/globalThemes-v9';

export enum DesignSystem {
  FluentV8 = 0,
  FluentV9 = 1,
  Material = 2,
}

export enum ColorMode {
  Light = 1,
  Dark = 2,
}

interface ThemeContextValue {
  system: DesignSystem;
  mode: ColorMode;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface IUnifiedThemeProviderProps {
  system: DesignSystem;
  mode: ColorMode;
  children: ReactNode;
}

export const UnifiedThemeProvider = (props: IUnifiedThemeProviderProps) => {
  const createFluentTheme = (): Theme => {
    const themeBase = props.mode === ColorMode.Dark ? webDarkTheme : webLightTheme;
    const themeApp = props.mode === ColorMode.Dark ? darkThemeV9 : lightThemeV9;

    //apply the app theme to the base theme
    const theme: Theme = {
      ...themeBase,
      ...themeApp,
    };

    return theme;
  };

  const fluentTheme = useMemo<Theme | undefined>(
    () => (props.system === DesignSystem.FluentV9 ? createFluentTheme() : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.system, props.mode],
  );

  let providerTree: ReactNode;

  switch (props.system) {
    case DesignSystem.FluentV9:
      providerTree = (
        <FluentProvider theme={fluentTheme}>
          <PortalCompatProvider>{props.children}</PortalCompatProvider>
        </FluentProvider>
      );
      break;
    default:
      providerTree = (
        <ThemeProvider applyTo="body" theme={props.mode === ColorMode.Dark ? darkTheme : lightTheme}>
          {props.children}
        </ThemeProvider>
      );
      break;
  }

  return (
    <ThemeContext.Provider value={{ system: props.system, mode: props.mode }}>{providerTree}</ThemeContext.Provider>
  );
};

export const useAppTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useAppTheme must be used within UnifiedThemeProvider');
  }

  return ctx;
};
